import { useEffect } from "react";
import { useMap } from "react-leaflet";
import { touchDebug } from "explorer/touch-debug.ts";

// Safari pinches the page (or, at minimum zoom, shrinks the tab into the tab overview) with
// its own gesture events, regardless of user-scalable=0. Leaflet only cancels a touchstart
// that already carries both fingers, which is rare as fingers land one after the other.
// So every multi-touch event and Safari's gesture events are cancelled on document level
// (Safari makes document touch listeners passive unless told otherwise) whenever a finger
// is on the map.
const SAFARI_GESTURE_EVENTS = ["gesturestart", "gesturechange", "gestureend"];
const MULTI_TOUCH_EVENTS = ["touchstart", "touchmove"] as const;
const GUARD_OPTIONS = { capture: true, passive: false };

const HINT_CLASS = "mmf-two-finger-hint";
const HINT_MIN_MOVE = 10; // px, so that tapping a marker doesn't flash the hint
const HINT_DURATION = 1500; // ms

/**
 * Touch gestures on the explorer map.
 *
 * Standalone: Leaflet's defaults, one finger pans, two fingers pan and zoom.
 * Embedded (iframe): one finger scrolls the host page, two fingers pan and zoom the map
 * (Leaflet's touchZoom follows the midpoint of both fingers). The CSS keeps touch-action at
 * pan-x pan-y (leaflet-mmf-styles.css), here single touches are kept from Leaflet's dragging,
 * which stays enabled for the mouse. Swiping with one finger shows a hint.
 */
export function MapGestures({ embedded }: { embedded: boolean }) {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();
    const parent = container.parentElement;
    const onMap = (e: TouchEvent) =>
      Array.from(e.touches).some((touch) =>
        container.contains(touch.target as Node),
      );

    // pinching started on the map, gesture events carry no touches
    let pinching = false;
    const cancelMultiTouch = (e: TouchEvent) => {
      pinching = e.touches.length > 1 && onMap(e);
      if (pinching) e.preventDefault();
    };
    const cancelGesture = (e: Event) => {
      if (pinching || container.contains(e.target as Node)) e.preventDefault();
    };
    const endPinch = (e: TouchEvent) => {
      if (e.touches.length < 2) pinching = false;
    };

    MULTI_TOUCH_EVENTS.forEach((type) =>
      document.addEventListener(type, cancelMultiTouch, GUARD_OPTIONS),
    );
    SAFARI_GESTURE_EVENTS.forEach((type) =>
      document.addEventListener(type, cancelGesture, GUARD_OPTIONS),
    );
    document.addEventListener("touchend", endPinch, true);
    document.addEventListener("touchcancel", endPinch, true);

    let start: { x: number; y: number } | undefined;
    let hintTimeout: ReturnType<typeof setTimeout> | undefined;

    // capturing on the parent runs before any listener on the container or inside of it
    const onTouchStart = (e: TouchEvent) => {
      if (!container.contains(e.target as Node)) return;
      if (e.touches.length === 1) {
        e.stopPropagation();
        start = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      } else {
        start = undefined; // no hint for the rest of a two-finger gesture
      }
    };
    const onTouchMove = (e: TouchEvent) => {
      if (!start || e.touches.length !== 1) return;
      const touch = e.touches[0];
      if (
        Math.hypot(touch.clientX - start.x, touch.clientY - start.y) <
        HINT_MIN_MOVE
      )
        return;
      container.classList.add(HINT_CLASS);
      clearTimeout(hintTimeout);
      hintTimeout = setTimeout(
        () => container.classList.remove(HINT_CLASS),
        HINT_DURATION,
      );
    };

    if (embedded && parent) {
      parent.addEventListener("touchstart", onTouchStart, { capture: true });
      container.addEventListener("touchmove", onTouchMove, { passive: true });
    }

    const stopDebug = touchDebug(map);

    return () => {
      MULTI_TOUCH_EVENTS.forEach((type) =>
        document.removeEventListener(type, cancelMultiTouch, GUARD_OPTIONS),
      );
      SAFARI_GESTURE_EVENTS.forEach((type) =>
        document.removeEventListener(type, cancelGesture, GUARD_OPTIONS),
      );
      document.removeEventListener("touchend", endPinch, true);
      document.removeEventListener("touchcancel", endPinch, true);
      parent?.removeEventListener("touchstart", onTouchStart, {
        capture: true,
      });
      container.removeEventListener("touchmove", onTouchMove);
      clearTimeout(hintTimeout);
      container.classList.remove(HINT_CLASS);
      stopDebug();
    };
  }, [map, embedded]);

  return null;
}
